import { Controller } from "@hotwired/stimulus"

// Drives the shared Daily Routine Guidance day strip. The strip stays outside
// the replaceable checklist frame, so selection moves immediately while Turbo
// fetches only the chosen day's content below it.
export default class extends Controller {
  static targets = [
    "day",
    "placeholder",
    "content",
    "dateTitle",
    "emptyMarker",
    "progressIndicator",
    "progressRing",
    "completionMark"
  ]

  connect() {
    this.animationFrames = []
    this.animationTimeouts = new Map()
    this.preserveAnimatedStyle = this.preserveAnimatedStyle.bind(this)
    document.addEventListener("turbo:before-morph-attribute", this.preserveAnimatedStyle)
  }

  disconnect() {
    this.animationFrames?.forEach((frame) => cancelAnimationFrame(frame))
    this.animationTimeouts?.forEach((timeout) => clearTimeout(timeout))
    document.removeEventListener("turbo:before-morph-attribute", this.preserveAnimatedStyle)
  }

  preserveAnimatedStyle(event) {
    if (event.detail.attributeName !== "style") return
    if (this.animationTimeouts.has(event.target)) event.preventDefault()
  }

  animateOnNextPaint(callback) {
    const firstFrame = requestAnimationFrame(() => {
      const secondFrame = requestAnimationFrame(callback)
      this.animationFrames.push(secondFrame)
    })
    this.animationFrames.push(firstFrame)
  }

  phaseCompletion(event) {
    const day = this.dayTargets.find((candidate) => candidate.classList.contains("is-selected"))
    if (!day) return

    day.dataset[`${event.detail.phase}Complete`] = event.detail.logged ? "true" : "false"
    this.renderProgress(day)
  }

  renderProgress(day) {
    const morningComplete = day.dataset.morningComplete === "true"
    const eveningComplete = day.dataset.eveningComplete === "true"
    const completedPhases = [ morningComplete, eveningComplete ].filter(Boolean).length
    const hasProgress = completedPhases > 0
    const complete = completedPhases === 2
    const progressOffset = [ "62.83", "31.42", "0" ][completedPhases]
    const emptyMarker = day.querySelector('[data-routine-day-strip-target="emptyMarker"]')
    const indicator = day.querySelector('[data-routine-day-strip-target="progressIndicator"]')
    const ring = day.querySelector('[data-routine-day-strip-target="progressRing"]')
    const completionMark = day.querySelector('[data-routine-day-strip-target="completionMark"]')
    const wasHidden = indicator?.classList.contains("hidden")

    emptyMarker?.classList.toggle("hidden", hasProgress)
    indicator?.classList.toggle("hidden", !hasProgress)

    if (ring) {
      ring.dataset.progressOffset = progressOffset
      if (hasProgress && wasHidden) {
        this.animateProgressRing(ring)
      } else {
        ring.style.strokeDashoffset = progressOffset
        this.holdAnimatedStyle(ring, 600)
      }
    }

    if (completionMark) {
      completionMark.dataset.complete = complete ? "true" : "false"
      if (complete && wasHidden) {
        this.animateCompletionMark(completionMark)
      } else {
        completionMark.style.opacity = complete ? "1" : "0"
        completionMark.style.transform = complete ? "scale(1)" : "scale(0.75)"
        this.holdAnimatedStyle(completionMark, 400)
      }
    }

    const state = complete ? "Complete" : morningComplete ? "Partial morning" : eveningComplete ? "Partial evening" : day.dataset.emptyLabel
    day.setAttribute("aria-label", `${day.dataset.dateLabel}; ${state}`)
  }

  animateProgressRing(ring) {
    ring.style.transition = "none"
    ring.style.strokeDashoffset = "62.83"
    this.holdAnimatedStyle(ring, 600)

    this.animateOnNextPaint(() => {
      ring.style.removeProperty("transition")
      ring.style.strokeDashoffset = ring.dataset.progressOffset
    })
  }

  animateCompletionMark(mark) {
    mark.style.transition = "none"
    mark.style.opacity = "0"
    mark.style.transform = "scale(0.75)"
    this.holdAnimatedStyle(mark, 400)

    this.animateOnNextPaint(() => {
      const complete = mark.dataset.complete === "true"
      mark.style.removeProperty("transition")
      mark.style.opacity = complete ? "1" : "0"
      mark.style.transform = complete ? "scale(1)" : "scale(0.75)"
    })
  }

  holdAnimatedStyle(element, duration) {
    const previousTimeout = this.animationTimeouts.get(element)
    if (previousTimeout) clearTimeout(previousTimeout)

    const timeout = setTimeout(() => this.animationTimeouts.delete(element), duration)
    this.animationTimeouts.set(element, timeout)
  }

  select(event) {
    const day = event.currentTarget

    // Re-tapping the day already in view: don't reload or flash a skeleton.
    if (day.classList.contains("is-selected")) {
      event.preventDefault()
      return
    }

    this.moveSelectionTo(day)
    this.updateHeading(day)
    this.showPlaceholder()
  }

  moveSelectionTo(day) {
    this.dayTargets.forEach((other) => {
      const selected = other === day
      other.classList.toggle("is-selected", selected)
      if (selected) {
        other.setAttribute("aria-current", "date")
      } else {
        other.removeAttribute("aria-current")
      }
    })
  }

  // The Today header lives above this controller's element (outside the strip and
  // the :routine_today frame), so move its title + date to the tapped day by id.
  // It is server-rendered to the agenda's date, so a refresh on a past-day URL
  // stays correct; this only makes the tap feel instant.
  updateHeading(day) {
    const title = document.getElementById("routine-guidance-heading-title")
    const date = document.getElementById("routine-guidance-heading-date")
    if (title) title.textContent = day.dataset.headingTitle
    if (date) date.textContent = day.dataset.headingDate
    if (this.hasDateTitleTarget) this.dateTitleTarget.textContent = day.dataset.selectedDateTitle
  }

  // Swap the live checklist for the skeleton while the frame is in flight.
  showPlaceholder() {
    if (this.hasContentTarget) this.contentTarget.classList.add("hidden")
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove("hidden")
  }
}
